// src/components/CreditUsageAlert.js
// ---------------------------------------------------------------------------
// Phase 6 (FINAL) + Phase 7 UX delta — inline credit-usage alert shown
// directly above the chat typing/input area.
//
// Only two product states produce an alert:
//   * CRITICAL (90% <= usage < 100%): compact, DISMISSIBLE banner with a
//     close (×) button. Once dismissed it stays suppressed for the current
//     credit-allocation period (91/95/99% do NOT re-show it). Reaching 100%
//     still transitions to the persistent exhausted banner.
//   * EXHAUSTED (100% / server reports unavailable): PERSISTENT banner with
//     NO close button. The CTA/copy is subscription-aware (Phase 7):
//       - active subscription -> "View Subscription Details" -> /billing
//       - no active subscription -> "Subscribe" -> /billing
//     The subscription state comes from the server-authoritative usage
//     response (hasActiveSubscription) — never inferred client-side.
//
// Nothing is shown below 90%. No toast, no modal, no header indicator, no
// dollar values, no model/provider names.
// ---------------------------------------------------------------------------
import React from 'react';
import { Box, Button, CloseButton, Flex, Text } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useCreditUsageContext } from '../context/CreditContext';
import {
  CRITICAL_ALERT_COPY,
  resolveChatAlert,
} from '../utils/creditHelpers';

export default function CreditUsageAlert() {
  const credit = useCreditUsageContext();
  const navigate = useNavigate();

  const alert = resolveChatAlert(credit.state, credit.dismissed90, credit.hasActiveSubscription);
  if (!alert) return null;

  if (alert.kind === 'exhausted') {
    const copy = alert.copy;
    return (
      <Flex
        align="center"
        justify="space-between"
        gap={3}
        bg="#FFF5F5"
        border="1px solid #FEB2B2"
        borderRadius="12px"
        px={4}
        py={2.5}
        mb={2}
        role="alert"
        aria-label="AI credit exhausted"
      >
        <Box>
          <Text fontSize="13px" fontWeight="600" color="#9B2C2C">
            {copy.title}
          </Text>
          <Text fontSize="13px" color="#742A2A">
            {copy.body}
          </Text>
        </Box>
        <Button
          size="sm"
          flexShrink={0}
          bg="#CF008A"
          color="#FFFFFF"
          borderRadius="10px"
          fontWeight="600"
          _hover={{ bg: '#D933A1' }}
          onClick={() => navigate(copy.actionHref)}
        >
          {copy.actionLabel}
        </Button>
      </Flex>
    );
  }

  // CRITICAL — dismissible.
  return (
    <Flex
      align="center"
      justify="space-between"
      gap={3}
      bg="#FFF7ED"
      border="1px solid #FED7AA"
      borderRadius="12px"
      px={4}
      py={2.5}
      mb={2}
      role="alert"
      aria-label="Credit usage warning"
    >
      <Text fontSize="13px" color="#7C2D12">
        {CRITICAL_ALERT_COPY.title} {CRITICAL_ALERT_COPY.body}
      </Text>
      <CloseButton
        size="sm"
        flexShrink={0}
        color="#7C2D12"
        aria-label="Dismiss credit usage warning"
        onClick={credit.dismiss90}
      />
    </Flex>
  );
}
